<script setup lang="ts">
import { type TriggerEvent } from "floating-vue";
import { type Component, computed, ref } from "vue";

import ShortcutStrokes from "~/components/dumb/ShortcutStrokes.vue";
import GlobalPopper, { PopperType } from "~/components/globalPopper";
import { getCommandsMap, getStrokesForPlatform } from "~/constants/command";
import { type CommandId, Placement } from "~/shared/enums";
import { useAppStore, usePageStore } from "~/stores";

const props = defineProps<{
  disabled?: boolean;
  showOnMobile?: boolean;
  noWrapper?: boolean;
  placement?: Placement;
  commandId?: CommandId;
  commandIds?: CommandId[];
  hideStrokes?: boolean;
  text?: string[] | string;
  component?: Component;
  componentArgs?: object;
  block?: boolean;
  heightBlock?: boolean;
  showDelay?: number;
  info?: boolean;
  disableClickHide?: boolean;
  skidding?: number;
  distance?: number;
  referenceNode?: Component | Element;
  sections?: { title?: string; items: string[] }[];
  image?: string;
  imageText?: string;
}>();

const appStore = useAppStore();
const pageStore = usePageStore();

const commandsMap = computed(() => getCommandsMap(appStore.commandsList));
const tooltip = ref<InstanceType<typeof GlobalPopper> | null>(null);

const content = computed(() => (props.text === undefined ? [] : Array.isArray(props.text) ? props.text : [props.text]));
const commands = computed(() => [
  ...(props.commandId === undefined ? [] : [props.commandId]),
  ...(props.commandIds ?? []),
]);

const placementDefinite = computed(() => props.placement ?? Placement.BOTTOM);

const show = () => {
  if (props.disabled) {
    return;
  }

  tooltip.value?.show();
};

const hide = () => {
  tooltip.value?.hide();
};

defineExpose({
  show,
  hide,
});
</script>

<template>
  <GlobalPopper
    ref="tooltip"
    :type="PopperType.TOOLTIP"
    :reference-node="() => referenceNode as Element"
    :disabled="disabled || (pageStore.isMobile && !showOnMobile)"
    :no-wrapper="noWrapper"
    :placement="placementDefinite"
    :skidding="skidding"
    :distance="distance ?? 7"
    :hide-triggers="
      (events: TriggerEvent[]) => [
        ...(disableClickHide ? events.filter((x) => x !== 'click') : events),
        ...(info || disableClickHide ? [] : (['click'] satisfies TriggerEvent[])),
      ]
    "
    :popper-triggers="info ? ['hover'] : []"
    :delay="{ show: showDelay ?? 500, hide: 0 }"
    :theme="`tooltip-${pageStore.theme}`"
    class="flex"
    :class="{
      'max-w-full grow': block,
      'h-full': heightBlock,
    }">
    <template #default="{ triggerRef }">
      <slot :trigger-ref="triggerRef" />
    </template>

    <template #popper>
      <div
        class="flex gap-1 rounded-sm border border-md bg-lt px-1.5 py-1 text-xs text-lt"
        :class="info ? 'max-w-[240px]' : 'max-w-xs'">
        <div v-if="image" class="flex flex-col gap-1.5 p-1">
          <img :src="image" class="h-24 w-40" alt="" />
          <span v-if="imageText" class="w-40 break-words hyphens-auto">{{ imageText }}</span>
        </div>
        <div v-else-if="commands.length || content.length" class="flex w-full flex-col gap-1">
          <div v-for="(command, index) in commands" :key="index" class="flex w-full items-center justify-between gap-2">
            {{ commandsMap.get(command)?.title }}
            <ShortcutStrokes
              v-if="!hideStrokes"
              :strokes="getStrokesForPlatform(appStore.commandsList, command, pageStore.isMac)" />
          </div>
          <span v-for="(line, index) in content" :key="index" class="min-h-[16px] break-words hyphens-auto">
            {{ line }}
          </span>
        </div>
        <div v-else-if="sections" class="flex w-full flex-col gap-3 p-1">
          <div
            v-for="(section, index) in sections"
            :key="index"
            class="flex w-full flex-col justify-between gap-1 text-xs select-none">
            <div v-if="section.title" class="font-semibold text-vlt">{{ section.title }}</div>
            <ul class="flex list-outside list-disc flex-col gap-1 pl-4">
              <li v-for="(line, itemIndex) in section.items" :key="itemIndex" class="break-words hyphens-auto">
                {{ line }}
              </li>
            </ul>
          </div>
        </div>
        <component :is="component" v-if="component" v-bind="componentArgs" />
      </div>
    </template>
  </GlobalPopper>
</template>

<style>
@reference "#frontend.css";
.v-popper__popper {
  z-index: 10010;
}
.v-popper--theme-tooltip .v-popper__wrapper {
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
}
.v-popper--theme-tooltip .v-popper__inner {
  padding: 0px;
  border-radius: 4px;
  background-color: transparent;
}
.v-popper--theme-tooltip-light .v-popper__arrow-outer {
  @apply border-app-light-border-md;
}
.v-popper--theme-tooltip-dark .v-popper__arrow-outer {
  @apply border-app-dark-border-md;
}
</style>
